(() => {
  'use strict';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const feature = (fn) => { try { fn(); } catch (err) { console.error(err); } };
  // FEATURES
  feature(() => { // megaMenu
    const nav = document.querySelector('[data-mega]');
    if (!nav) return;
    const header = nav.closest('[data-header]');
    const triggers = [...nav.querySelectorAll('[data-mega-trigger]')];
    const panelFor = (t) => document.getElementById(t.getAttribute('aria-controls'));
    let current = null, openTimer = 0, closeTimer = 0, openedAt = 0;
    const clear = () => { clearTimeout(openTimer); clearTimeout(closeTimer); };
    const close = (returnFocus) => {
      clear();
      if (!current) return;
      current.setAttribute('aria-expanded', 'false');
      panelFor(current).removeAttribute('data-open');
      header?.removeAttribute('data-menu-open');
      if (returnFocus) current.focus();
      current = null;
    };
    const open = (t) => {
      clear();
      if (current === t) return;
      close(false);
      t.setAttribute('aria-expanded', 'true');
      panelFor(t).setAttribute('data-open', '');
      header?.setAttribute('data-menu-open', '');
      current = t;
      openedAt = performance.now();
    };
    triggers.forEach((t) => t.addEventListener('click', (e) => {
      // hover may have opened it a moment before this click: keep it open
      if (current === t && performance.now() - openedAt < 400) return;
      if (current === t) return close(false);
      open(t);
      if (e.detail === 0) { // keyboard activation: move focus into the panel
        const panel = panelFor(t);
        const target = panel.querySelector('[role="tab"][aria-selected="true"]') || panel.querySelector('a[href]');
        target?.focus();
      }
    }));
    if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
      triggers.forEach((t) => [t, panelFor(t)].forEach((el) => {
        el.addEventListener('pointerenter', () => { clear(); openTimer = setTimeout(() => open(t), 80); });
        el.addEventListener('pointerleave', () => { clear(); closeTimer = setTimeout(() => {
          if (current && panelFor(current).contains(document.activeElement)) return;
          close(false);
        }, 150); });
      }));
    }
    matchMedia('(min-width: 1024px)').addEventListener('change', (e) => { if (!e.matches) close(false); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && current) close(true); });
    document.addEventListener('pointerdown', (e) => { if (current && !nav.contains(e.target)) close(false); });
    nav.addEventListener('focusout', (e) => { if (e.relatedTarget && !nav.contains(e.relatedTarget)) close(false); });

    nav.querySelectorAll('[role="tablist"]').forEach((list) => {
      const tabs = [...list.querySelectorAll('[role="tab"]')];
      const select = (tab, focus) => {
        tabs.forEach((x) => {
          const on = x === tab;
          x.setAttribute('aria-selected', String(on));
          x.tabIndex = on ? 0 : -1;
          document.getElementById(x.getAttribute('aria-controls')).hidden = !on;
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab, false));
        tab.addEventListener('pointerenter', () => select(tab, false));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'Home' || e.key === 'End') {
            e.preventDefault();
            return select(e.key === 'Home' ? tabs[0] : tabs[tabs.length - 1], true);
          }
          const step = { ArrowDown: 1, ArrowUp: -1 }[e.key];
          if (!step) return;
          e.preventDefault();
          select(tabs[(i + step + tabs.length) % tabs.length], true);
        });
      });
    });
  });

  feature(() => { // mobileNav
    const drawer = document.querySelector('[data-drawer]');
    const opener = document.querySelector('[data-drawer-open]');
    if (!drawer || !opener) return;
    const focusables = () => [...drawer.querySelectorAll('a[href], button, summary')].filter((el) => {
      if (el.offsetParent === null) return false;
      const d = el.closest('details');
      return !d || d.open || el.tagName === 'SUMMARY' && el.parentElement === d;
    });
    const close = (returnFocus) => {
      if (drawer.hidden) return;
      drawer.hidden = true;
      document.body.style.overflow = '';
      opener.setAttribute('aria-expanded', 'false');
      if (returnFocus) opener.focus();
    };
    const open = () => {
      drawer.hidden = false;
      document.body.style.overflow = 'hidden';
      opener.setAttribute('aria-expanded', 'true');
      drawer.querySelector('[data-drawer-close]').focus();
    };
    opener.addEventListener('click', open);
    drawer.querySelector('[data-drawer-close]').addEventListener('click', () => close(true));
    drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') return close(true);
      if (e.key !== 'Tab') return;
      const f = focusables(), first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    matchMedia('(min-width: 1024px)').addEventListener('change', (e) => { if (e.matches) close(false); });
  });

  feature(() => { // reveal
    const items = document.querySelectorAll('[data-reveal]');
    if (!items.length || reduced || !('IntersectionObserver' in window)) return;
    document.documentElement.classList.add('reveal-on');
    const io = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      io.unobserve(entry.target);
    }), { rootMargin: '0px 0px -10% 0px' });
    items.forEach((el) => io.observe(el));
  });

  feature(() => { // counters
    const items = document.querySelectorAll('[data-count-to]');
    if (!items.length || reduced || !('IntersectionObserver' in window)) return;
    const run = (el) => {
      const to = Number(el.dataset.countTo), suffix = el.dataset.suffix || '', final = el.textContent;
      const start = performance.now(), duration = 1200;
      const tick = (now) => {
        const t = Math.min((now - start) / duration, 1);
        el.textContent = Math.round(to * (1 - Math.pow(1 - t, 3))) + suffix;
        if (t < 1) requestAnimationFrame(tick); else el.textContent = final;
      };
      requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      run(entry.target);
      io.unobserve(entry.target);
    }), { threshold: 0.5 });
    items.forEach((el) => io.observe(el));
  });

  feature(() => { // shots: one switch pauses every animated screenshot on a case-study page
    const btns = [...document.querySelectorAll('[data-shots-toggle]')];
    if (!btns.length) return;
    if (reduced) { btns.forEach((b) => { b.hidden = true; }); return; }
    btns.forEach((btn) => btn.addEventListener('click', () => {
      const paused = !document.documentElement.hasAttribute('data-shots-paused');
      document.documentElement.toggleAttribute('data-shots-paused', paused);
      btns.forEach((b) => { b.setAttribute('aria-pressed', String(paused)); b.textContent = paused ? 'Play motion' : 'Pause motion'; });
    }));
  });

  feature(() => { // copy: a [data-copy] button copies the code block beside it (style guide)
    document.querySelectorAll('[data-copy]').forEach((btn) => btn.addEventListener('click', () => {
      const code = btn.parentElement.querySelector('code');
      if (!code || !navigator.clipboard) return;
      navigator.clipboard.writeText(code.textContent).then(() => {
        btn.textContent = 'Copied';
        setTimeout(() => { btn.textContent = 'Copy'; }, 1600);
      });
    }));
  });

  feature(() => { // toc: the article's "On this page" list, built from its h2 headings (ids are added where missing)
    const box = document.querySelector('[data-toc]');
    const body = document.querySelector('[data-article-body]');
    if (!box || !body) return;
    const heads = [...body.querySelectorAll('h2')].filter((h) => h.textContent.trim());
    if (heads.length < 3) return;
    const list = box.querySelector('[data-toc-list]');
    const used = new Set();
    heads.forEach((h) => {
      let id = h.id || h.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'section';
      while (used.has(id) || (!h.id && document.getElementById(id))) id += '-2';
      used.add(id);
      h.id = id;
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${id}`;
      link.textContent = h.textContent.trim();
      link.className = 'flex min-h-11 items-center rounded-lg px-3 text-[15px] text-fg-muted transition-colors duration-[180ms] hover:bg-ink-800 hover:text-brand-500';
      item.append(link);
      list.append(item);
    });
    const count = box.querySelector('[data-toc-count]');
    if (count) count.textContent = `${heads.length} sections`;
    box.hidden = false;
  });

  feature(() => { // share: article share links point at this page; the link button copies its address
    const rows = document.querySelectorAll('[data-share-row]');
    if (!rows.length) return;
    const url = encodeURIComponent(location.href.split('#')[0]);
    const title = encodeURIComponent(document.title.split(' | ')[0]);
    const targets = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      x: `https://x.com/intent/post?url=${url}&text=${title}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      whatsapp: `https://wa.me/?text=${title}%20${url}`,
    };
    rows.forEach((row) => {
      row.querySelectorAll('[data-share]').forEach((link) => { if (targets[link.dataset.share]) link.href = targets[link.dataset.share]; });
      const copy = row.querySelector('[data-share-copy]');
      const status = row.querySelector('[data-share-status]');
      if (!copy || !navigator.clipboard) { if (copy) copy.hidden = true; return; }
      copy.addEventListener('click', () => navigator.clipboard.writeText(location.href.split('#')[0]).then(() => {
        status.textContent = 'Link copied';
        setTimeout(() => { status.textContent = ''; }, 2000);
      }));
    });
  });

  feature(() => { // marqueeToggle: starts the logo rows and gives them one Pause/Play button
    const btn = document.querySelector('[data-marquee-toggle]');
    const rows = [...document.querySelectorAll('[data-marquee]')];
    if (!btn || !rows.length) return;
    if (reduced) { btn.hidden = true; return; }
    btn.hidden = false;
    rows.forEach((row) => row.setAttribute('data-marquee-on', ''));
    btn.addEventListener('click', () => {
      const paused = btn.getAttribute('aria-pressed') !== 'true';
      rows.forEach((row) => row.toggleAttribute('data-paused', paused));
      btn.setAttribute('aria-pressed', String(paused));
      btn.textContent = paused ? 'Play' : 'Pause';
    });
  });

  feature(() => { // consoleTabs
    const list = document.querySelector('[data-console-tabs]');
    if (!list) return;
    const tabs = [...list.querySelectorAll('[role="tab"]')];
    const select = (tab, focus) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        document.getElementById(t.getAttribute('aria-controls')).hidden = !on;
      });
      if (focus) tab.focus();
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(tab, false));
      tab.addEventListener('keydown', (e) => {
        const last = tabs.length - 1;
        const next = { ArrowRight: i === last ? 0 : i + 1, ArrowLeft: i === 0 ? last : i - 1, Home: 0, End: last }[e.key];
        if (next === undefined) return;
        e.preventDefault();
        select(tabs[next], true);
      });
    });
  });

  feature(() => { // serviceExplorer
    const root = document.querySelector('[data-explorer]');
    if (!root) return;
    const triggers = [...root.querySelectorAll('[data-explorer-trigger]')];
    if (!triggers.length) return;
    const panelFor = (t) => document.getElementById(t.getAttribute('aria-controls'));
    const open = (t) => triggers.forEach((x) => {
      const on = x === t;
      x.setAttribute('aria-expanded', String(on));
      panelFor(x).hidden = !on;
    });
    root.setAttribute('data-explorer-ready', '');
    open(triggers[0]);
    triggers.forEach((t) => t.addEventListener('click', () => open(t)));
  });

  feature(() => { // typed: types through the phrases in data-typed (a JSON array), then rests on the first one
    document.querySelectorAll('[data-typed]').forEach((el) => {
      let words;
      try { words = JSON.parse(el.dataset.typed); } catch { return; }
      if (!Array.isArray(words) || words.length < 2 || reduced) return;
      const loops = Number(el.dataset.typedLoops || 2); // full passes through the list before it stops; 0 = never stop
      const line = el.parentElement;
      let w = 0, n = words[0].length, deleting = true, passes = 0;
      line.classList.add('typed-on');
      const tick = () => {
        n += deleting ? -1 : 1;
        el.textContent = words[w].slice(0, n);
        let delay = deleting ? 45 : 90;
        if (!deleting && n === words[w].length) {
          if (w === 0 && loops && ++passes >= loops) { line.classList.remove('typed-on'); return; }
          deleting = true;
          delay = 1800;
        } else if (deleting && n === 0) {
          deleting = false;
          w = (w + 1) % words.length;
          delay = 300;
        }
        timer = setTimeout(tick, delay);
      };
      // typing only runs while the headline is on screen and the tab is visible
      let timer = 0, seen = true;
      const sync = () => {
        clearTimeout(timer);
        timer = 0;
        if (seen && document.visibilityState === 'visible' && line.classList.contains('typed-on')) timer = setTimeout(tick, 1800);
      };
      if ('IntersectionObserver' in window) new IntersectionObserver(([entry]) => { seen = entry.isIntersecting; sync(); }).observe(line);
      document.addEventListener('visibilitychange', sync);
      timer = setTimeout(tick, 2200);
    });
  });

  feature(() => { // slider: crossfading screenshots with dots, arrows and a pause button
    document.querySelectorAll('[data-slider]').forEach((root) => {
      const slides = [...root.querySelectorAll('[data-slide]')];
      const dots = [...root.querySelectorAll('[data-slide-dot]')];
      const toggle = root.querySelector('[data-slider-toggle]');
      if (slides.length < 2) return;
      let index = 0, timer = 0, playing = !reduced;
      const show = (i) => {
        index = (i + slides.length) % slides.length;
        slides.forEach((slide, n) => {
          slide.toggleAttribute('data-active', n === index);
          slide.setAttribute('aria-hidden', String(n !== index));
        });
        dots.forEach((dot, n) => dot.setAttribute('aria-current', String(n === index)));
      };
      const schedule = () => {
        clearInterval(timer);
        if (!playing) return;
        timer = setInterval(() => {
          // only advance while the slider is on screen and nobody is pointing at or focused in it
          if (root.offsetParent !== null && !root.matches(':hover, :focus-within')) show(index + 1);
        }, 3500);
      };
      root.querySelector('[data-slider-prev]')?.addEventListener('click', () => show(index - 1));
      root.querySelector('[data-slider-next]')?.addEventListener('click', () => show(index + 1));
      dots.forEach((dot, n) => dot.addEventListener('click', () => show(n)));
      if (toggle && !reduced) {
        toggle.hidden = false;
        toggle.addEventListener('click', () => {
          playing = !playing;
          toggle.setAttribute('aria-pressed', String(!playing));
          toggle.setAttribute('aria-label', playing ? 'Pause slideshow' : 'Play slideshow');
          schedule();
        });
      }
      root.setAttribute('data-slider-ready', '');
      show(0);
      schedule();
    });
  });

  feature(() => { // carousel: a scroll-snap row with previous/next buttons (no auto-play; swipe and keyboard scrolling still work)
    document.querySelectorAll('[data-carousel-root]').forEach((root) => {
      const track = root.querySelector('[data-carousel]');
      const nav = root.querySelector('[data-carousel-nav]');
      const prev = root.querySelector('[data-carousel-prev]');
      const next = root.querySelector('[data-carousel-next]');
      if (!track || !nav || !prev || !next) return;
      const step = () => {
        if (root.dataset.carouselStep === 'page') return track.clientWidth * 0.8; // rows of small items (filter tags) move most of a screenful
        const item = track.children[0];
        return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        nav.hidden = max <= 1;
        prev.disabled = track.scrollLeft <= 1;
        next.disabled = track.scrollLeft >= max - 1;
      };
      const move = (dir) => track.scrollBy({ left: dir * step(), behavior: reduced ? 'auto' : 'smooth' });
      prev.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      let frame = 0;
      const queue = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); };
      track.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue);
      update();
    });
  });

  feature(() => { // video: anything with data-video opens its video in a popup; a review with data-video gets a play button
    const PLAY = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" class="icon size-5" aria-hidden="true"><path d="M240,128a15.74,15.74,0,0,1-7.6,13.51L88.32,229.65a16,16,0,0,1-16.2.3A15.86,15.86,0,0,1,64,216.13V39.87a15.86,15.86,0,0,1,8.12-13.82,16,16,0,0,1,16.2.3L232.4,114.49A15.74,15.74,0,0,1,240,128Z"/></svg>';
    const CLOSE = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" class="icon size-5" aria-hidden="true"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg>';
    document.querySelectorAll('[data-review][data-video]').forEach((card) => {
      const who = (card.querySelector('.text-fg') || {}).textContent || 'this client';
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.video = card.dataset.video;
      button.dataset.videoTitle = `Video review from ${who}`;
      card.removeAttribute('data-video');
      if (card.dataset.videoPoster) {
        button.className = 'video-thumb';
        const img = new Image(640, 360);
        img.src = card.dataset.videoPoster;
        img.alt = '';
        img.loading = 'lazy';
        button.append(img);
        button.insertAdjacentHTML('beforeend', `<span class="video-play">${PLAY}</span><span class="sr-only">Play video review from ${who}</span>`);
      } else {
        button.className = 'video-link';
        button.innerHTML = `<span class="video-play">${PLAY}</span>Watch the video`;
      }
      card.prepend(button);
    });

    let dialog, frame;
    const build = () => {
      dialog = document.createElement('dialog');
      dialog.className = 'video-modal';
      dialog.innerHTML = `<button type="button" class="video-close" aria-label="Close video">${CLOSE}</button><div class="video-frame"></div>`;
      frame = dialog.querySelector('.video-frame');
      dialog.querySelector('.video-close').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
      dialog.addEventListener('close', () => frame.replaceChildren()); // removing the player stops the video
      document.body.append(dialog);
    };
    const player = (url, title) => {
      const yt = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
      const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (yt || vimeo) {
        const el = document.createElement('iframe');
        el.src = yt ? `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&rel=0` : `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
        el.title = title;
        el.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
        el.allowFullscreen = true;
        return el;
      }
      const el = document.createElement('video');
      el.src = url;
      el.controls = true;
      el.autoplay = true;
      el.playsInline = true;
      el.setAttribute('aria-label', title);
      return el;
    };
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-video]');
      if (!trigger || !trigger.dataset.video || typeof HTMLDialogElement !== 'function') return;
      e.preventDefault();
      if (!dialog) build();
      const title = trigger.dataset.videoTitle || trigger.textContent.trim() || 'Video';
      dialog.setAttribute('aria-label', title);
      frame.replaceChildren(player(trigger.dataset.video, title));
      dialog.showModal();
    });
  });

  feature(() => { // flow: plays the workflow steps each time it comes into view; with data-flow-loop it repeats and gets a Pause button
    document.querySelectorAll('[data-flow]').forEach((root) => {
      const steps = [...root.querySelectorAll('[data-flow-step]')];
      if (!steps.length || reduced || !('IntersectionObserver' in window)) return;
      const loop = root.hasAttribute('data-flow-loop');
      const toggle = root.querySelector('[data-flow-toggle]');
      let timer = 0, again = 0, seen = false, paused = false;
      const set = (i) => steps.forEach((step, n) => step.setAttribute('data-state', n < i ? 'done' : n === i ? 'active' : 'todo'));
      const stop = () => { clearInterval(timer); clearTimeout(again); };
      const play = () => {
        stop();
        let i = 0;
        set(0);
        root.setAttribute('data-flow-running', '');
        timer = setInterval(() => {
          i++;
          set(i);
          if (i < steps.length) return;
          clearInterval(timer);
          root.removeAttribute('data-flow-running');
          if (loop) again = setTimeout(() => { if (seen && !paused) play(); }, 2600);
        }, loop ? 1300 : 900);
      };
      root.setAttribute('data-flow-ready', '');
      set(steps.length);
      new IntersectionObserver((entries) => entries.forEach((entry) => {
        seen = entry.isIntersecting;
        if (seen && !paused) play(); else if (loop) stop();
      })).observe(root);
      if (loop && toggle) { // [data-flow-toggle]
        toggle.hidden = false;
        toggle.addEventListener('click', () => {
          paused = !paused;
          toggle.textContent = paused ? 'Play' : 'Pause';
          toggle.setAttribute('aria-pressed', String(paused));
          if (paused) { stop(); set(steps.length); root.removeAttribute('data-flow-running'); } else play();
        });
      }
    });
  });

  feature(() => { // tour: the app mock's sidebar switches screens; it also walks through them on its own while on screen
    document.querySelectorAll('[data-tour]').forEach((root) => {
      const screens = [...root.querySelectorAll('[data-tour-screen]')];
      const navs = [...root.querySelectorAll('[data-tour-go]')];
      const caption = root.querySelector('[data-tour-caption]');
      const count = root.querySelector('[data-tour-count]');
      const crumb = root.querySelector('[data-tour-crumb-out]');
      const toggle = root.querySelector('[data-tour-toggle]');
      if (screens.length < 2) return;
      let index = 0, timer = 0, seen = false, paused = false;
      const show = (i, press) => {
        index = (i + screens.length) % screens.length;
        const screen = screens[index];
        screens.forEach((s, n) => { s.toggleAttribute('data-active', n === index); s.setAttribute('aria-hidden', String(n !== index)); });
        navs.forEach((nav) => {
          const on = nav.dataset.tourGo === screen.dataset.tourNav;
          if (on) nav.setAttribute('aria-current', 'true'); else nav.removeAttribute('aria-current');
          nav.toggleAttribute('data-tour-press', on && Boolean(press));
        });
        if (caption) caption.textContent = screen.dataset.tourText;
        if (count) count.textContent = `${index + 1} / ${screens.length}`;
        if (crumb) crumb.textContent = screen.dataset.tourCrumb;
      };
      const schedule = () => {
        clearInterval(timer);
        if (seen && !paused && !reduced) timer = setInterval(() => show(index + 1, true), 4200);
      };
      const setPaused = (value) => {
        paused = value;
        if (toggle) { toggle.textContent = paused ? 'Play' : 'Pause'; toggle.setAttribute('aria-pressed', String(paused)); }
        schedule();
      };
      navs.forEach((nav) => nav.addEventListener('click', () => {
        show(screens.findIndex((s) => s.dataset.tourScreen === nav.dataset.tourGo));
        setPaused(true); // the visitor took over
      }));
      if (toggle && !reduced) { toggle.hidden = false; toggle.addEventListener('click', () => setPaused(!paused)); }
      show(0);
      if ('IntersectionObserver' in window) new IntersectionObserver(([entry]) => { seen = entry.isIntersecting; schedule(); }, { threshold: 0.3 }).observe(root);
    });
  });

  feature(() => { // scene: shows the steps of a scripted run one after another (typing out any [data-scene-type] text), then replays
    document.querySelectorAll('[data-scene]').forEach((root) => {
      const steps = [...root.querySelectorAll('[data-scene-step]')];
      const log = root.querySelector('[data-scene-log]');
      const toggle = root.querySelector('[data-scene-toggle]');
      if (reduced || steps.length < 2 || !('IntersectionObserver' in window)) return; // all steps stay visible
      const texts = new Map(steps.map((step) => [step, step.querySelector('[data-scene-type]')]).filter(([, el]) => el).map(([, el]) => [el, el.textContent]));
      let run = 0, seen = false, paused = false, playing = false;
      const wait = (ms, id) => new Promise((resolve) => setTimeout(() => resolve(id === run), ms));
      const reset = () => {
        steps.forEach((step) => { step.removeAttribute('data-shown'); step.removeAttribute('data-done'); });
        texts.forEach((text, el) => { el.textContent = text; el.removeAttribute('data-scene-typing'); });
      };
      const finish = () => { // the complete conversation, used when paused
        run++;
        playing = false;
        steps.forEach((step) => { step.setAttribute('data-shown', ''); step.setAttribute('data-done', ''); });
        texts.forEach((text, el) => { el.textContent = text; el.removeAttribute('data-scene-typing'); });
        if (log) log.scrollTop = log.scrollHeight;
      };
      const play = async () => {
        const id = ++run;
        playing = true;
        reset();
        if (log) log.scrollTop = 0;
        if (!(await wait(600, id))) return;
        for (const [n, step] of steps.entries()) {
          if (n) steps[n - 1].setAttribute('data-done', '');
          step.setAttribute('data-shown', '');
          if (log) log.scrollTop = log.scrollHeight; // keep the newest step in view, like a chat
          const typed = step.querySelector('[data-scene-type]');
          if (typed) {
            const text = texts.get(typed);
            typed.setAttribute('data-scene-typing', '');
            for (let i = 0; i <= text.length; i++) {
              typed.textContent = text.slice(0, i);
              if (!(await wait(24, id))) return;
            }
            typed.removeAttribute('data-scene-typing');
          }
          if (!(await wait(Number(step.dataset.sceneWait || 1000), id))) return;
        }
        playing = false;
        if (seen && !paused) play();
      };
      root.setAttribute('data-scene-on', '');
      reset();
      if (toggle) {
        toggle.hidden = false;
        toggle.addEventListener('click', () => {
          paused = !paused;
          toggle.textContent = paused ? 'Play' : 'Pause';
          toggle.setAttribute('aria-pressed', String(paused));
          if (paused) finish(); else play();
        });
      }
      new IntersectionObserver(([entry]) => {
        seen = entry.isIntersecting;
        if (seen && !paused && !playing) play();
      }, { threshold: 0.25 }).observe(root);
    });
  });

  feature(() => { // starter: the chosen need updates the proof line and the button label
    document.querySelectorAll('[data-starter]').forEach((root) => {
      const chips = [...root.querySelectorAll('[data-need]')];
      const proof = root.querySelector('[data-starter-proof]');
      const cta = root.querySelector('[data-starter-cta]');
      if (!chips.length || !proof || !cta) return;
      chips.forEach((chip) => chip.addEventListener('click', () => {
        chips.forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
        proof.textContent = chip.dataset.proof;
        cta.textContent = `Start your ${chip.dataset.need} project`;
      }));
    });
  });

  // bindForm: inline validation, an error summary, then hands the request to the visitor's email app. Used by every [data-contact-form].
  const bindForm = (form) => {
    const summary = form.querySelector('[data-form-summary]');
    const success = form.querySelector('[data-form-success]');
    if (!summary || !success) return;
    const list = summary.querySelector('ul');
    const fields = [...form.querySelectorAll('input, select, textarea')];
    form.noValidate = true; // our messages replace the browser's bubbles
    const problem = (field) => {
      if (field.validity.valueMissing) return field.dataset.required || 'This field is required.';
      if (field.validity.typeMismatch) return field.dataset.invalid || 'Check the format of this field.';
      return '';
    };
    const check = (field) => {
      const message = problem(field);
      const error = document.getElementById(`${field.id}-error`);
      if (error) { error.textContent = message; error.hidden = !message; }
      field.setAttribute('aria-invalid', String(Boolean(message)));
      return message;
    };
    fields.forEach((field) => {
      field.addEventListener('blur', () => check(field));
      field.addEventListener('input', () => { if (field.getAttribute('aria-invalid') === 'true') check(field); });
    });
    const native = form.hasAttribute('data-native'); // a Shopify {% form %}: validate here, then let it post to the store
    form.addEventListener('submit', (e) => {
      if (!native) e.preventDefault();
      const errors = fields.map((field) => [field, check(field)]).filter(([, message]) => message);
      success.hidden = true;
      if (errors.length) {
        list.replaceChildren(...errors.map(([field, message]) => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = `#${field.id}`;
          link.textContent = `${field.dataset.label}: ${message}`;
          link.addEventListener('click', (ev) => { ev.preventDefault(); field.focus(); });
          item.append(link);
          return item;
        }));
        e.preventDefault();
        summary.hidden = false;
        (errors.length > 1 ? summary : errors[0][0]).focus();
        return;
      }
      summary.hidden = true;
      if (native) return;
      const value = (name) => (form.elements[name] ? form.elements[name].value.trim() : '');
      const body = fields.filter((field) => field.value.trim()).map((field) => `${field.dataset.label}: ${field.value.trim()}`).join('\n');
      // a form can name its own subject (data-subject); the contact page builds one from the chosen service
      const subject = form.dataset.subject ? `${form.dataset.subject} (${value('name')})` : `Project enquiry: ${value('need')} (${value('name')})`;
      location.href = `mailto:info@tetralogicx.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      success.hidden = false;
      success.focus();
    });
  };

  feature(() => { // contactForm
    document.querySelectorAll('[data-contact-form]').forEach(bindForm);
  });

  feature(() => { // auditPopup: any [data-audit] button opens the audit request form in a popup (its href is the fallback)
    const STAR = '<span class="text-brand-500" aria-hidden="true"> *</span>';
    const input = (id, label, type, attrs, required) => `<div><label for="audit-${id}" class="block font-medium text-fg">${label}${required ? STAR : ''}</label><input id="audit-${id}" name="${id}" type="${type}" aria-describedby="audit-${id}-error" data-label="${label}" ${attrs} class="field mt-2 block w-full"><p id="audit-${id}-error" class="field-error" hidden></p></div>`;
    const select = (id, label, options) => `<div><label for="audit-${id}" class="block font-medium text-fg">${label}</label><select id="audit-${id}" name="${id}" aria-describedby="audit-${id}-error" data-label="${label}" class="field mt-2 block w-full">${options.map((o) => `<option>${o}</option>`).join('')}</select><p id="audit-${id}-error" class="field-error" hidden></p></div>`;
    const TOPICS = ['Full store audit', 'Speed and Core Web Vitals', 'Conversion and checkout', 'SEO and content', 'AI and agent readiness', 'Apps and tech stack'];
    let dialog, form;
    const build = () => {
      dialog = document.createElement('dialog');
      dialog.className = 'audit-modal';
      dialog.setAttribute('aria-labelledby', 'audit-title');
      dialog.innerHTML = `<form data-contact-form data-subject="Free audit request" action="mailto:info@tetralogicx.com" method="post" enctype="text/plain">
        <div class="flex items-start justify-between gap-4">
          <div><p class="font-mono text-[13px] uppercase tracking-[0.08em] text-brand-500">Free audit</p><h2 id="audit-title" class="mt-2 text-2xl lg:text-3xl">Tell us about your store</h2><p class="mt-2 text-[15px]">We review it and reply within 24 hours with written findings. Required fields are marked with <span class="text-brand-500">*</span></p></div>
          <button type="button" data-audit-close aria-label="Close" class="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-fg transition-colors duration-[180ms] hover:bg-overlay"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" class="icon size-5" aria-hidden="true"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg></button>
        </div>
        <div data-form-summary role="alert" tabindex="-1" hidden class="form-summary mt-5 rounded-card p-4"><p class="font-semibold text-fg">Please fix the following</p><ul class="mt-2 list-disc space-y-1 pl-6"></ul></div>
        <div class="mt-6 grid gap-5 sm:grid-cols-2">
          ${input('name', 'Full name', 'text', 'autocomplete="name" required data-required="Enter your name so we know who to reply to."', true)}
          ${input('email', 'Business email', 'email', 'autocomplete="email" inputmode="email" spellcheck="false" required data-required="Enter your email so we can send the audit." data-invalid="That email looks incomplete. Use the form name@company.com."', true)}
          ${input('website', 'Store or website address', 'text', 'autocomplete="url" inputmode="url" spellcheck="false" placeholder="yourstore.com" required data-required="Enter the store address you want audited."', true)}
          ${input('phone', 'Phone or WhatsApp (optional)', 'tel', 'autocomplete="tel" inputmode="tel"', false)}
          ${select('platform', 'Platform', ['Shopify', 'Shopify Plus', 'WooCommerce', 'Magento', 'Custom build', 'Not sure'])}
          ${select('topic', 'What should we audit?', TOPICS)}
          ${select('revenue', 'Monthly revenue (optional)', ['Prefer not to say', 'Under $10K', '$10K–$50K', '$50K–$250K', '$250K–$1M', '$1M+'])}
          <div class="sm:col-span-2"><label for="audit-notes" class="block font-medium text-fg">Anything we should know? (optional)</label><textarea id="audit-notes" name="notes" rows="3" aria-describedby="audit-notes-error" data-label="Notes" class="field mt-2 block w-full"></textarea><p id="audit-notes-error" class="field-error" hidden></p></div>
        </div>
        <div class="mt-6 flex flex-wrap items-center gap-4"><button type="submit" class="btn btn-primary">Request my free audit</button><p class="text-sm">Your details stay with us. We never share or sell them.</p></div>
        <p data-form-success role="status" tabindex="-1" hidden class="form-success mt-5 rounded-card p-4 text-fg">Your email app should have opened with the request ready to send. Press send there and we will reply within 24 hours.</p>
      </form>`;
      form = dialog.querySelector('form');
      bindForm(form);
      dialog.querySelector('[data-audit-close]').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
      document.body.append(dialog);
    };
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-audit]');
      if (!trigger || typeof HTMLDialogElement !== 'function') return; // without <dialog> the link goes to the contact page
      e.preventDefault();
      if (!dialog) {
        dialog = document.querySelector('dialog.audit-modal');
        if (dialog) {
          form = dialog.querySelector('form');
          dialog.querySelector('[data-audit-close]').addEventListener('click', () => dialog.close());
          dialog.addEventListener('click', (ev) => { if (ev.target === dialog) dialog.close(); });
        } else build();
      }
      const topic = trigger.dataset.audit;
      const topicField = form.querySelector('[data-audit-topic]') || form.elements.topic;
      if (topicField && TOPICS.includes(topic)) topicField.value = topic;
      dialog.showModal();
      (form.querySelector('[data-audit-first]') || form.elements.name).focus();
    });
  });

  feature(() => { // postFilter: topic chips and pagination on the insights page; ?cat=, ?q= and ?page= in the address preset them
    const buttons = [...document.querySelectorAll('[data-post-filter]')];
    if (!buttons.length) return;
    const posts = [...document.querySelectorAll('[data-post-cat]')];
    const list = document.querySelector('[data-post-list]');
    const status = document.querySelector('[data-post-status]');
    const empty = document.querySelector('[data-post-empty]');
    const note = document.querySelector('[data-post-query]');
    const noteText = note && note.querySelector('[data-post-query-text]');
    const pager = document.querySelector('[data-post-pages]');
    const numbers = pager && pager.querySelector('[data-page-numbers]');
    const prev = pager && pager.querySelector('[data-page-prev]');
    const next = pager && pager.querySelector('[data-page-next]');
    const range = pager && pager.querySelector('[data-page-range]');
    const size = Math.max(1, Number(list && list.dataset.pageSize) || posts.length);
    let topic = 'all', query = '', page = 1;
    const apply = (scroll) => {
      const words = query.toLowerCase().split(/\s+/).filter(Boolean);
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.postFilter === topic)));
      const matches = posts.filter((post) => (topic === 'all' || post.dataset.postCat === topic) && words.every((w) => post.textContent.toLowerCase().includes(w)));
      const pages = Math.max(1, Math.ceil(matches.length / size));
      page = Math.min(Math.max(1, page), pages);
      const from = (page - 1) * size, visible = matches.slice(from, from + size);
      posts.forEach((post) => { post.hidden = !visible.includes(post); });
      const summary = matches.length ? `Showing ${from + 1}–${from + visible.length} of ${matches.length} ${matches.length === 1 ? 'article' : 'articles'}` : 'Showing 0 articles';
      if (status) status.textContent = summary;
      if (empty) empty.hidden = matches.length > 0;
      if (note) { note.hidden = !query || matches.length === 0; if (noteText) noteText.textContent = `“${query}”`; }
      if (pager) {
        pager.hidden = pages < 2;
        prev.disabled = page === 1;
        next.disabled = page === pages;
        range.textContent = summary;
        numbers.replaceChildren(...Array.from({ length: pages }, (_, n) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.textContent = String(n + 1);
          b.setAttribute('aria-label', `Page ${n + 1}`);
          if (n + 1 === page) b.setAttribute('aria-current', 'page');
          b.className = 'page-number';
          b.addEventListener('click', () => { page = n + 1; apply(true); });
          return b;
        }));
      }
      if (scroll && list) list.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' });
    };
    buttons.forEach((btn) => btn.addEventListener('click', () => { topic = btn.dataset.postFilter; page = 1; apply(); }));
    document.querySelectorAll('[data-post-reset]').forEach((reset) => reset.addEventListener('click', () => { topic = 'all'; query = ''; page = 1; apply(); buttons[0].focus(); }));
    if (pager) {
      prev.addEventListener('click', () => { page--; apply(true); });
      next.addEventListener('click', () => { page++; apply(true); });
    }
    const params = new URLSearchParams(location.search);
    const cat = params.get('cat'), q = (params.get('q') || '').trim();
    if (cat && buttons.some((b) => b.dataset.postFilter === cat)) topic = cat;
    if (q) query = q;
    page = Number(params.get('page')) || 1;
    apply();
  });

  feature(() => { // workFilter: portfolio tags (one scrolling line), grid/list switch and a "show more" button; a #category in the address presets the tag
    const buttons = [...document.querySelectorAll('[data-work-filter]')];
    if (!buttons.length) return;
    const items = [...document.querySelectorAll('[data-work-item]')];
    const list = document.querySelector('[data-work-list]');
    const more = document.querySelector('[data-work-more]');
    const status = document.querySelector('[data-work-status]');
    const views = [...document.querySelectorAll('[data-work-view]')];
    const size = Math.max(1, Number(list && list.dataset.pageSize) || 12);
    const alias = { 'food-beverage': 'f-b', 'international': 'all' };
    let topic = 'all', limit = size;
    const apply = () => {
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.workFilter === topic)));
      const matches = items.filter((item) => topic === 'all' || item.dataset.cat.split(' ').includes(topic));
      items.forEach((item) => { item.hidden = !matches.slice(0, limit).includes(item); });
      const shown = Math.min(limit, matches.length);
      if (status) status.textContent = `Showing ${shown} of ${matches.length} ${matches.length === 1 ? 'project' : 'projects'}`;
      if (more) { more.hidden = shown >= matches.length; more.textContent = `Show ${Math.min(size, matches.length - shown)} more projects`; }
    };
    const choose = (key, reveal) => {
      const wanted = alias[key] || key;
      const btn = buttons.find((b) => b.dataset.workFilter === wanted);
      if (!btn) return false;
      topic = wanted;
      limit = size;
      apply();
      if (reveal) btn.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduced ? 'auto' : 'smooth' }); // bring the chosen tag into view on its line
      return true;
    };
    buttons.forEach((btn) => btn.addEventListener('click', () => choose(btn.dataset.workFilter, false)));
    document.querySelectorAll('[data-work-jump]').forEach((jump) => jump.addEventListener('click', () => {
      if (choose(jump.dataset.workJump, true) && list) list.closest('section').scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' });
    }));
    views.forEach((view) => view.addEventListener('click', () => {
      list.dataset.view = view.dataset.workView;
      views.forEach((v) => v.setAttribute('aria-pressed', String(v === view)));
    }));
    if (more) more.addEventListener('click', () => {
      const first = items.filter((item) => !item.hidden).length;
      limit += size;
      apply();
      const next = items.filter((item) => !item.hidden)[first];
      if (next) { const link = next.querySelector('a'); if (link) link.focus({ preventScroll: true }); }
    });
    const fromHash = () => choose(decodeURIComponent(location.hash.slice(1)), true);
    window.addEventListener('hashchange', fromHash);
    if (!fromHash()) apply();
  });

  // Everything the search popup can open. Only pages that exist on this site are listed; hrefs are relative to the site root.
  const SEARCH_INDEX = [
    { t: 'Shopify Development', d: 'Custom themes, integrations and migrations', k: 'service', tag: 'Shopify', h: 'pages/shopify-development' },
    { t: 'Agentic Commerce', d: 'Sell inside ChatGPT, Copilot and Gemini', k: 'service', tag: 'AI', h: 'pages/shopify-agentic-commerce-1' },
    { t: 'Shopify Plus', d: 'Shopify Plus development for brands that scale past $2M', k: 'service', tag: 'Shopify', h: 'pages/shopify-plus-development' },
    { t: 'Store Setup & Launch', d: 'Shopify store builds that launch in 4–8 weeks', k: 'service', tag: 'Shopify', h: 'pages/shopify-setup-launch' },
    { t: 'Shopify Migration', d: 'Shopify migrations that protect rankings and revenue', k: 'service', tag: 'Shopify', h: 'pages/shopify-migration' },
    { t: 'Shopify App Development', d: 'Custom Shopify apps that launch in 4–8 weeks', k: 'service', tag: 'Shopify', h: 'pages/shopify-app-development' },
    { t: 'Headless Commerce', d: 'Headless Shopify for brands that have outgrown Liquid', k: 'service', tag: 'Shopify', h: 'pages/headless-commerce' },
    { t: 'Ecommerce Integrations', d: 'Connect your stack so orders, inventory and data stay in sync', k: 'service', tag: 'Shopify', h: 'pages/ecommerce-integration' },
    { t: 'Web Development', d: 'Custom web development that scales past launch that scales past launch', k: 'service', tag: 'Web', h: 'pages/web-development' },
    { t: 'Custom Web Applications', d: 'Custom web applications that solve real business problems', k: 'service', tag: 'Web', h: 'pages/custom-web-application' },
    { t: 'Corporate Websites', d: 'Corporate websites that look credible and convert visitors', k: 'service', tag: 'Web', h: 'pages/corporate-websites' },
    { t: 'React & Next.js', d: 'React & Next.js storefronts that perform, scale and convert', k: 'service', tag: 'Web', h: 'pages/react-next-js' },
    { t: 'Backend Development', d: 'Backends that scale with your commerce stack', k: 'service', tag: 'Web', h: 'pages/backend-development' },
    { t: 'API Development', d: 'APIs that scale with your commerce stack', k: 'service', tag: 'Web', h: 'pages/api-development' },
    { t: 'MVP Development', d: 'Ship the real product. Not a throwaway prototype', k: 'service', tag: 'Software', h: 'pages/mvp-development' },
    { t: 'SaaS Development', d: 'Build the product. Not just another app shell', k: 'service', tag: 'Software', h: 'pages/saas-development' },
    { t: 'Product Discovery', d: 'Know what to build. Before you spend on it', k: 'service', tag: 'Software', h: 'pages/product-discovery' },
    { t: 'UI/UX Design', d: 'UI/UX design that moves revenue, not just pixels', k: 'service', tag: 'Software', h: 'pages/ui-ux-design-1' },
    { t: 'Platform Modernization', d: 'Modernize without breaking what works', k: 'service', tag: 'Software', h: 'pages/platform-modrenization' },
    { t: 'iOS Development', d: 'Native iOS apps that convert better than mobile web', k: 'service', tag: 'Mobile', h: 'pages/ios-development' },
    { t: 'Android Development', d: 'Native Android apps that convert better than mobile web', k: 'service', tag: 'Mobile', h: 'pages/android-development' },
    { t: 'React Native', d: 'One codebase. iOS and Android that convert', k: 'service', tag: 'Mobile', h: 'pages/react-native' },
    { t: 'Flutter', d: 'One codebase. iOS and Android that convert', k: 'service', tag: 'Mobile', h: 'pages/flutter' },
    { t: 'Shopify Mobile App', d: 'Shopify mobile apps that convert 3.2× better than web', k: 'service', tag: 'Mobile', h: 'pages/shopify-mobile-app-ecommerce' },
    { t: 'AI & Automation', d: 'AI agents that actually ship revenue', k: 'service', tag: 'AI', h: 'pages/ai-automation-solutions' },
    { t: 'AI Agents', d: 'AI agents that actually get work done', k: 'service', tag: 'AI', h: 'pages/ai-agents' },
    { t: 'AI Chatbots', d: 'AI chatbots that resolve issues, not just chat', k: 'service', tag: 'AI', h: 'pages/ai-chatbots' },
    { t: 'Customer Support Bots', d: 'Customer support bots that resolve tickets before they pile up', k: 'service', tag: 'AI', h: 'pages/customer-support-bots' },
    { t: 'WhatsApp Automation', d: 'WhatsApp automation that turns conversations into revenue', k: 'service', tag: 'AI', h: 'pages/whatsapp-automation' },
    { t: 'Workflow Automation', d: 'Workflow automation that removes the manual grind', k: 'service', tag: 'AI', h: 'pages/workflow-automation' },
    { t: 'AI Integrations', d: 'AI integrations that actually move the numbers', k: 'service', tag: 'AI', h: 'pages/ai-integrations' },
    { t: 'SEO', d: 'Shopify SEO that ranks, converts and compounds', k: 'service', tag: 'Marketing', h: 'pages/seo' },
    { t: 'Technical SEO', d: 'Technical SEO that makes Google love your store', k: 'service', tag: 'Marketing', h: 'pages/technical-seo' },
    { t: 'Ecommerce SEO', d: 'Ecommerce SEO that turns search into sales', k: 'service', tag: 'Marketing', h: 'pages/ecommerce-seo' },
    { t: 'PPC & Google Ads', d: 'Google Ads that actually turn spend into revenue', k: 'service', tag: 'Marketing', h: 'pages/ppc-google-ads' },
    { t: 'Social Media Marketing', d: 'Social media that drives revenue, not just likes', k: 'service', tag: 'Marketing', h: 'pages/social-media-marketing' },
    { t: 'Content Marketing', d: 'Content that ranks, educates and sells', k: 'service', tag: 'Marketing', h: 'pages/content-marketing' },
    { t: 'Conversion Optimization', d: 'CRO that turns more traffic into revenue', k: 'service', tag: 'Growth', h: 'pages/conversion-optimization' },
    { t: 'Store Audit', d: 'A store audit that shows exactly where revenue is leaking', k: 'service', tag: 'Growth', h: 'pages/store-audit' },
    { t: 'Speed Optimization', d: 'Shopify speed optimization that hits sub-2.5s LCP', k: 'service', tag: 'Growth', h: 'pages/shopify-speed-optimization-1' },
    { t: 'Support & Maintenance', d: 'Shopify retainers that replace 2 house devs', k: 'service', tag: 'Growth', h: 'pages/support-and-maintenance' },
    { t: 'Ecommerce Development', d: 'Shopify stores that convert, scale and stay fast', k: 'service', tag: 'Shopify', h: 'pages/ecommerce-development' },
    { t: 'Custom Software Development', d: 'Custom software that scales to enterprise', k: 'service', tag: 'Software', h: 'pages/custom-software-development' },
    { t: 'Mobile App Development', d: 'Mobile apps that users actually open daily', k: 'service', tag: 'Mobile', h: 'pages/mobile-app-development' },
    { t: 'Digital Marketing', d: 'Digital marketing that ships revenue, not impressions', k: 'service', tag: 'Marketing', h: 'pages/digital-marketing-1' },
    { t: 'Shopify CRO Audit', d: 'Shopify CRO that ships measurable revenue lift ships measurable revenue lift', k: 'service', tag: 'Growth', h: 'pages/shopify-cro-audit' },
    { t: 'Privacy Policy', d: 'What we collect and what we do with it', k: 'company', tag: 'Company', h: 'pages/privacy-policy' },
    { t: 'Terms of Service', d: 'The rules for using this site', k: 'company', tag: 'Company', h: 'pages/terms-of-service' },
    { t: 'Sitemap', d: 'Every page in one place', k: 'company', tag: 'Company', h: 'pages/sitemap' },
    { t: 'Partnership program', d: 'Co-build, refer, integrate or join the ecosystem', k: 'company', tag: 'Company', h: 'pages/partnership-program' },
    { t: 'All services', d: 'Shopify, AI automation, mobile apps and growth', k: 'service', tag: 'Services', h: 'pages/services' },
    { t: 'Free store audit', d: 'Five prioritised fixes within 48 hours', k: 'service', tag: 'Growth', h: 'pages/contact-us' },
    { t: 'Ordexa', d: 'Order management for COD-first merchants', k: 'product', tag: 'Product', h: 'products/ordexa-orders-managment' },
    { t: 'Tracklify', d: 'Branded order tracking and post-purchase updates', k: 'product', tag: 'Product', h: 'products/tracklify' },
    { t: 'Checkout Studio', d: 'Checkout upsells, custom fields and branding', k: 'product', tag: 'Product', h: 'products/checkout-studio' },
    { t: 'Wishlist+ & Back in stock', d: 'Wishlists with restock and price-drop alerts', k: 'product', tag: 'Product', h: 'products/wishlist-wishlist-back-in-stock' },
    { t: 'Variantly', d: 'Product options beyond Shopify limits', k: 'product', tag: 'Product', h: 'products/variantly-product-variants' },
    { t: 'Cartlift', d: 'Upsells and bundles that lift AOV', k: 'product', tag: 'Product', h: 'products/cartlift' },
    { t: 'All products', d: 'Six Shopify apps we build and run', k: 'product', tag: 'Products', h: 'pages/products' },
    { t: 'Portfolio', d: '80 live stores, apps and platforms', k: 'work', tag: 'Work', h: 'pages/portfolio' },
    { t: 'Case studies', d: 'Five engagements with the numbers attached', k: 'work', tag: 'Work', h: 'pages/case-studies' },
    { t: 'Clinikally', d: 'Shopify Plus build for a teledermatology brand', k: 'work', tag: 'Case study', h: 'pages/case-studies' },
    { t: 'Goom', d: 'Multi-region Plus build with Hebrew and English', k: 'work', tag: 'Case study', h: 'pages/case-studies' },
    { t: "Dana's Bakery", d: 'Perishables and drop-day architecture', k: 'work', tag: 'Case study', h: 'pages/case-studies' },
    { t: 'Fair Harbor', d: 'Redesign and checkout rebuild', k: 'work', tag: 'Case study', h: 'pages/case-studies' },
    { t: 'Loops Beauty', d: 'Quiz-driven subscription and native app', k: 'work', tag: 'Case study', h: 'pages/case-studies' },
    { t: 'The 12-point Shopify speed checklist', d: 'Get under 2.5s LCP', k: 'article', tag: 'Insights', h: 'blogs/insights/the-12-point-shopify-speed-checklist-that-gets-you-under-2-5s-lcp' },
    { t: 'Shopify vs Shopify Plus in 2026', d: 'Which one is right for your brand', k: 'article', tag: 'Insights', h: 'blogs/insights/shopify-vs-shopify-plus-in-2026-which-one-is-right-for-your-brand' },
    { t: 'The Shopify agentic commerce playbook', d: 'Be discoverable inside AI assistants', k: 'article', tag: 'Insights', h: 'blogs/insights/the-complete-shopify-agentic-commerce-playbook-for-2026' },
    { t: 'Top 20 Shopify apps for 2026', d: 'The apps every store needs', k: 'article', tag: 'Insights', h: 'blogs/insights/top-20-shopify-apps-every-ecommerce-store-needs-in-2026' },
    { t: 'Top 10 Shopify development trends', d: 'What to watch in 2026', k: 'article', tag: 'Insights', h: 'blogs/insights/top-10-shopify-development-trends-to-watch-in-2026' },
    { t: 'All insights', d: 'Field notes on Shopify, speed and AI commerce', k: 'article', tag: 'Insights', h: 'blogs/insights' },
    { t: 'About Tetralogicx', d: 'Story, values and how we work', k: 'company', tag: 'Company', h: 'pages/about-us' },
    { t: 'Contact', d: 'Start a project or book a call', k: 'company', tag: 'Company', h: 'pages/contact-us' },
  ];

  feature(() => { // search: a command palette — type to filter the site index, arrow keys to move, Enter to open, Esc to close
    const ICON = {
      service: 'M232,96a7.89,7.89,0,0,0-.3-2.2L217.35,43.6A16.07,16.07,0,0,0,202,32H54A16.07,16.07,0,0,0,38.65,43.6L24.31,93.8A7.89,7.89,0,0,0,24,96h0v16a40,40,0,0,0,16,32v72a8,8,0,0,0,8,8H208a8,8,0,0,0,8-8V144a40,40,0,0,0,16-32V96ZM54,48H202l11.42,40H42.61Zm50,56h48v8a24,24,0,0,1-48,0Zm-16,0v8a24,24,0,0,1-35.12,21.26,7.88,7.88,0,0,0-1.82-1.06A24,24,0,0,1,40,112v-8ZM200,208H56V151.2a40.57,40.57,0,0,0,8,.8,40,40,0,0,0,32-16,40,40,0,0,0,64,0,40,40,0,0,0,32,16,40.57,40.57,0,0,0,8-.8Zm4.93-75.8a8.08,8.08,0,0,0-1.8,1.05A24,24,0,0,1,168,112v-8h48v8A24,24,0,0,1,204.93,132.2Z', product: 'M223.68,66.15,135.68,18a15.88,15.88,0,0,0-15.36,0l-88,48.17a16,16,0,0,0-8.32,14v95.64a16,16,0,0,0,8.32,14l88,48.17a15.88,15.88,0,0,0,15.36,0l88-48.17a16,16,0,0,0,8.32-14V80.18A16,16,0,0,0,223.68,66.15ZM128,32l80.34,44-29.77,16.3-80.35-44ZM128,120,47.66,76l33.9-18.56,80.34,44ZM40,90l80,43.78v85.79L40,175.82Zm176,85.78h0l-80,43.79V133.82l32-17.51V152a8,8,0,0,0,16,0V107.55L216,90v85.77Z', work: 'M216,56H176V48a24,24,0,0,0-24-24H104A24,24,0,0,0,80,48v8H40A16,16,0,0,0,24,72V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V72A16,16,0,0,0,216,56ZM96,48a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96ZM216,72v41.61A184,184,0,0,1,128,136a184.07,184.07,0,0,1-88-22.38V72Zm0,128H40V131.64A200.19,200.19,0,0,0,128,152a200.25,200.25,0,0,0,88-20.37V200ZM104,112a8,8,0,0,1,8-8h32a8,8,0,0,1,0,16H112A8,8,0,0,1,104,112Z', article: 'M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,160H40V56H216V200ZM184,96a8,8,0,0,1-8,8H80a8,8,0,0,1,0-16h96A8,8,0,0,1,184,96Zm0,32a8,8,0,0,1-8,8H80a8,8,0,0,1,0-16h96A8,8,0,0,1,184,128Zm0,32a8,8,0,0,1-8,8H80a8,8,0,0,1,0-16h96A8,8,0,0,1,184,160Z', company: 'M240,208H224V96a16,16,0,0,0-16-16H144V32a16,16,0,0,0-24.88-13.32L39.12,72A16,16,0,0,0,32,85.34V208H16a8,8,0,0,0,0,16H240a8,8,0,0,0,0-16ZM208,96V208H144V96ZM48,85.34,128,32V208H48ZM112,112v16a8,8,0,0,1-16,0V112a8,8,0,1,1,16,0Zm-32,0v16a8,8,0,0,1-16,0V112a8,8,0,1,1,16,0Zm0,56v16a8,8,0,0,1-16,0V168a8,8,0,0,1,16,0Zm32,0v16a8,8,0,0,1-16,0V168a8,8,0,0,1,16,0Z',
    };
    const svg = (d, cls) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" class="${cls}" aria-hidden="true"><path d="${d}"/></svg>`;
    const esc = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    let dialog, input, list, heading, base = '', shown = [], active = 0;
    const render = () => {
      const words = input.value.toLowerCase().split(/\s+/).filter(Boolean);
      shown = SEARCH_INDEX.filter((item) => words.every((w) => `${item.t} ${item.d} ${item.tag}`.toLowerCase().includes(w)));
      active = 0;
      heading.textContent = words.length ? `${shown.length} ${shown.length === 1 ? 'result' : 'results'}` : 'Browse the site';
      list.innerHTML = shown.length
        ? shown.map((item, n) => `<a id="cmdk-${n}" role="option" aria-selected="${n === 0}" href="${base}${item.h}" class="cmdk-item"><span class="cmdk-icon">${svg(ICON[item.k], 'icon size-5')}</span><span class="min-w-0 flex-1"><span class="cmdk-title">${esc(item.t)}</span><span class="cmdk-desc">${esc(item.d)}</span></span><span class="cmdk-tag">${esc(item.tag)}</span></a>`).join('')
        : `<p class="cmdk-empty">Nothing matches “${esc(input.value)}”. <a href="${base}blogs/insights?q=${encodeURIComponent(input.value.trim())}" class="underline">Search the articles for it</a>.</p>`;
      input.setAttribute('aria-activedescendant', shown.length ? 'cmdk-0' : '');
    };
    const move = (to) => {
      if (!shown.length) return;
      active = (to + shown.length) % shown.length;
      [...list.children].forEach((el, n) => el.setAttribute('aria-selected', String(n === active)));
      input.setAttribute('aria-activedescendant', `cmdk-${active}`);
      list.children[active].scrollIntoView({ block: 'nearest' });
    };
    const build = () => {
      base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; // "/" or "/<locale>/": results follow the visitor's language
      dialog = document.createElement('dialog');
      dialog.className = 'search-modal';
      dialog.setAttribute('aria-label', 'Search the site');
      dialog.innerHTML = `<div class="cmdk-top">${svg('M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z', 'icon size-6 shrink-0 text-brand-500')}
          <input id="site-search" type="search" role="combobox" aria-expanded="true" aria-controls="cmdk-list" aria-autocomplete="list" aria-activedescendant="" autocomplete="off" spellcheck="false" placeholder="Search services, case studies, articles…" aria-label="Search the site" class="cmdk-input">
          <button type="button" data-search-close class="cmdk-key cursor-pointer" aria-label="Close search">Esc</button></div>
        <div class="cmdk-body"><p class="cmdk-heading"><span data-cmdk-heading>Browse the site</span></p><div id="cmdk-list" role="listbox" aria-label="Pages"></div></div>
        <div class="cmdk-foot"><p class="hidden items-center gap-4 sm:flex"><span><span class="cmdk-key">↑↓</span> Navigate</span><span><span class="cmdk-key">↵</span> Open</span><span><span class="cmdk-key">Esc</span> Close</span></p>
          <p>Can't find it? <a href="${base}pages/contact-us" class="inline-flex items-center gap-1 font-semibold text-brand-500">Ask us directly ${svg('M221.66,133.66l-72,72a8,8,0,0,1-11.32-11.32L196.69,136H40a8,8,0,0,1,0-16H196.69L138.34,61.66a8,8,0,0,1,11.32-11.32l72,72A8,8,0,0,1,221.66,133.66Z', 'icon size-4')}</a></p></div>`;
      input = dialog.querySelector('input');
      list = dialog.querySelector('#cmdk-list');
      heading = dialog.querySelector('[data-cmdk-heading]');
      input.addEventListener('input', render);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); move(active + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); move(active - 1); }
        else if (e.key === 'Enter') {
          e.preventDefault();
          if (shown.length) location.href = list.children[active].href;
          else if (input.value.trim()) location.href = `${base}blogs/insights?q=${encodeURIComponent(input.value.trim())}`;
        }
      });
      list.addEventListener('mousemove', (e) => { const item = e.target.closest('.cmdk-item'); if (item) { const n = [...list.children].indexOf(item); if (n !== active) move(n); } });
      dialog.querySelector('[data-search-close]').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
      document.body.append(dialog);
    };
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-search-open]');
      if (!trigger || typeof HTMLDialogElement !== 'function') return;
      if (!dialog) build();
      input.value = '';
      render();
      dialog.showModal();
      input.focus();
    });
  });

  feature(() => { // theme: light/dark toggle, remembered across pages (the inline script in <head> applies it before paint)
    const toggles = [...document.querySelectorAll('[data-theme-toggle]')];
    if (!toggles.length) return;
    const root = document.documentElement;
    const apply = (theme) => {
      if (theme === 'light') root.dataset.theme = 'light'; else delete root.dataset.theme;
      toggles.forEach((t) => t.setAttribute('aria-label', theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'));
      document.querySelectorAll('[data-theme-label]').forEach((label) => { label.textContent = theme === 'light' ? 'Dark mode' : 'Light mode'; });
    };
    apply(root.dataset.theme === 'light' ? 'light' : 'dark');
    toggles.forEach((toggle) => {
      toggle.hidden = false;
      toggle.addEventListener('click', () => {
        const next = root.dataset.theme === 'light' ? 'dark' : 'light';
        apply(next);
        try { localStorage.setItem('theme', next); } catch { /* storage blocked: the choice lasts for this page only */ }
      });
    });
  });
})();
